import { useBooleanState } from '@cian/react-utils';
import { ArticleParagraph3, Button, UIHeading5 } from '@cian/ui-kit';

import { clsx } from 'clsx';
import { type FC, useCallback, useState } from 'react';

import { useCardOfferIsEarlyAccessFlow } from '@/microfrontends/listing/shared/context/selectors';
import { useEarlyAccessFlow } from '@/microfrontends/listing/shared/hooks/card/useEarlyAccessFlow';

import { stopPropagation } from '@/packages/utils/event/stopPropagation';

import { usePhoneTracking } from '../../hooks';
import { type HandlePhonesShow, type PhoneButtonProps } from '../../types';

import styles from './PhoneButton.css';

export const PhoneButton: FC<PhoneButtonProps> = ({ phones, workHours, displayPhonesOptions }) => {
  const { state: opened, setTrue: setOpened } = useBooleanState();

  const { trackEarlyAccessConfirmed, trackEarlyAccessBlocked, trackPhoneButtonClick } = usePhoneTracking();

  const [isLoading, setIsLoading] = useState(false);

  const isEarlyAccessFlow = useCardOfferIsEarlyAccessFlow();

  const handleEarlyAccessConfirmed = useCallback(() => {
    trackEarlyAccessConfirmed();
    setOpened();
  }, [trackEarlyAccessConfirmed, setOpened]);

  const handleEarlyAccessFlow = useEarlyAccessFlow({
    onConfirmed: handleEarlyAccessConfirmed,
    onBlocked: trackEarlyAccessBlocked,
  });

  const handlePhonesShow = useCallback<HandlePhonesShow>(async () => {
    if (isEarlyAccessFlow) {
      setIsLoading(true);
      await handleEarlyAccessFlow();
      setIsLoading(false);

      return;
    }

    setOpened();
    trackPhoneButtonClick();
  }, [handleEarlyAccessFlow, isEarlyAccessFlow, setOpened, trackPhoneButtonClick]);

  if (!phones.length) {
    return null;
  }

  if (opened) {
    const { countToDisplay, shouldDisplayWorkHours } = displayPhonesOptions;

    return (
      <div className={clsx(styles['phones'], styles['phones-animation'])} data-testid="publisher.phone-button.expanded">
        {phones.slice(0, countToDisplay).map(phone => (
          <UIHeading5 data-mark="PhoneValue" key={phone} onClick={stopPropagation}>
            {phone}
          </UIHeading5>
        ))}
        {shouldDisplayWorkHours && workHours && <ArticleParagraph3 color="gray60_100">{workHours}</ArticleParagraph3>}
      </div>
    );
  }

  return (
    <Button
      fullWidth
      data-testid="publisher.phone-button"
      loading={isLoading}
      size="XS"
      theme="fill_primary"
      onClick={handlePhonesShow}
    >
      {`${phones[0].slice(0, -2)}...`}
    </Button>
  );
};
